import { cn } from '@/util/cn'
import Link from 'next/link'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { Icon } from 'ui'
import IconButton from 'ui/src/components/controls/icon-button'

type Option = {
    label: ReactNode
    icon?: string
    /**
     * Include hrefs in the toggle
     * This is useful for SEO
     * Currently this does not render correctly for users, but it works for bots
     */
    href?: string
    gradientClassName?: string
}

type ToggleProps = {
    className?: string
    options: Option[]
    index: number
    onClick?: (index: number) => void
    // use this if you need to navigate during the transition
    // if you try to update the url during an animation, it will cancel the animation
    onTransitionEnd?: (index: number) => void
}
const Toggle = ({ className, options, onClick, index, onTransitionEnd }: ToggleProps) => {
    const [_index, setIndex] = useState(index)
    const isTransitioning = useRef(false)
    useEffect(() => {
        if (!isTransitioning.current && index !== _index) {
            setIndex(index)
        }
    }, [_index, index, setIndex])
    const buttonWidth = `${100 * (1 / options.length)}%`
    const left = `${100 * (_index / options.length)}%`
    const gradient = ['bg-gradient-primary', 'bg-gradient-secondary', 'bg-gradient-tertiary']
    return (
        <div
            className={cn(
                'relative flex justify-between rounded-3xl bg-gradient-grey font-bold text-giphyWhite',
                className
            )}
        >
            <>
                <div
                    className={cn(
                        'absolute left-0 h-full rounded-[inherit] transition-all duration-250',
                        options[index].gradientClassName || gradient[_index % options.length]
                    )}
                    onTransitionEnd={() => {
                        onTransitionEnd?.(_index)
                        isTransitioning.current = false
                    }}
                    style={{
                        width: buttonWidth,
                        left,
                    }}
                />
                {options.map((option, mapIndex) => {
                    const handleClick = () => {
                        if (_index !== mapIndex) {
                            isTransitioning.current = true
                            setIndex(mapIndex)
                            onClick?.(mapIndex)
                        }
                    }

                    const buttonContent = (
                        <>
                            {option.icon && <IconButton inverted icon={option.icon as Icon} />}
                            {option.label}
                        </>
                    )

                    return option.href ? (
                        <Link
                            replace
                            onClick={handleClick}
                            className="relative flex cursor-pointer items-center justify-center gap-1 rounded-3xl p-2 text-center text-inherit"
                            key={mapIndex}
                            style={{ width: buttonWidth }}
                            href={option.href}
                        >
                            {buttonContent}
                        </Link>
                    ) : (
                        <button
                            onClick={handleClick}
                            className="relative flex cursor-pointer items-center justify-center gap-1 rounded-3xl p-2 text-inherit"
                            key={mapIndex}
                            type="button"
                            style={{ width: buttonWidth }}
                        >
                            {buttonContent}
                        </button>
                    )
                })}
            </>
        </div>
    )
}
export default Toggle
